/**
 * Apple Ads 自定义盯盘策略：新建 / 编辑弹层（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；
 * 复用 leo-card / leo-input / leo-select / leo-cta-primary / leo-btn-ghost 类）。
 *
 * 已读 docs/frontend_ui_guidline/leo-frontend-design.md v1.0（§6.1 按钮层级
 * （保存为本弹层唯一主 CTA）；§6.3 表单控件（深底输入 + focus 橙）；
 * §6.6 弹窗浮层（遮罩 + 居中面板，ESC / 点击遮罩 = 取消））。
 *
 * 行为（PR-AT-20260922-006）：
 *   - 条件组 1~5 行，组内 AND：每行 = 指标类型 select →（数值：时间周期 select
 *     + 指标 select 15 项；属性：时间周期位「—」禁用 + 指标 select 6 项
 *     （5 数值属性 + 1 枚举属性））→ 操作符 select → 阈值 number input；
 *     多值枚举（present_hour 当天小时段，PR-AT-20261023-001）阈值改由行内
 *     24 小时 chips 多选面板承载（操作符显示 ∈ / ∉）；
 *     「+ 添加条件」达 5 行禁用，仅 >1 行时可移除。
 *   - 操作类型 select：出价降低 / 出价提升 → 出现百分比 input（(0,100) 开区间）；
 *     暂停投放 / 预警 → 无数值位（action_value 提交 null；预警仅记录命中，
 *     供后续告警推送消费 is_alerted 预留列）。
 *   - 保存前前端校验同后端口径（条件数 / metric-period 联动 / 阈值可解析 /
 *     调提价百分比区间）；校验失败 Toast，不提交。
 *   - 提交由父组件完成（POST 新建 / PUT 编辑），本组件只产出 SaveWatchStrategyBody。
 *
 * 阈值模式（PR-AT-20260923-009；011 起扩至 total_sxfl；013 起 cost_ent_appl
 *   增「目标成本×」——仅 sxfl / total_sxfl / cost_ent_appl 条件行展示模式 select）：
 *   - 固定值（默认）：阈值直取输入值；
 *   - 目标费率×（仅 sxfl / total_sxfl）：生效阈值 = 目标费率 × 输入倍数 x
 *    （x 须为正数），行内实时展示换算结果「= 0.012」（目标费率由父组件
 *     targetFeilv 传入）；
 *   - 目标成本×（仅 cost_ent_appl，PR-AT-20260923-013）：生效阈值 = 目标成本
 *     cost_value（元）× 输入倍数 x（x 须为正数），行内实时展示换算结果
 *    「= 6000」（目标成本由父组件 targetCost 传入）；
 *   - 切出上述倍率允许指标时强制回落固定值模式（倍率仅与对应基准同纲有意义）。
 */

import { useEffect, useState } from "react";
import {
  ATTRIBUTE_METRIC_OPTIONS,
  ACTION_TYPE_OPTIONS,
  COST_MULTIPLIER_ALLOWED_METRICS,
  COST_VALUE_MODE_OPTIONS,
  ENUM_ATTRIBUTE_METRIC_OPTIONS,
  ENUM_METRIC_VALUES,
  ENUM_OPERATOR_OPTIONS,
  MAX_CONDITIONS,
  MULTIPLIER_ALLOWED_METRICS,
  MULTI_VALUE_ENUM_METRIC_KEYS,
  NUMERIC_METRIC_OPTIONS,
  OPERATOR_OPTIONS,
  PERIOD_OPTIONS,
  VALUE_MODE_FIXED,
  VALUE_MODE_OPTIONS,
  VALUE_MODE_TARGET_COST_MULTIPLIER,
  VALUE_MODE_TARGET_SXFL_MULTIPLIER,
  formatThreshold,
  type ConditionItem,
  type LabelOption,
  type MetricType,
  type SaveWatchStrategyBody,
  type ValueMode,
  type WatchStrategyRecord,
} from "../../services/apple/watchStrategy";
import { pushToast } from "../../hooks/useToast";

/** 条件行本地编辑态（阈值以 string 承载输入中间态，提交时转 number；
 *  多值枚举指标用 values 承载选中集合）。 */
interface ConditionRow {
  /** 行稳定 key（仅本地渲染用）。 */
  key: number;
  metric_type: MetricType;
  /** 数值/衍生指标的时间周期；属性指标恒为 null。 */
  period: string | null;
  metric: string;
  operator: string;
  value: string;
  /** 多值枚举（present_hour）选中的枚举值集合（提交时按注册表定义序归一）。 */
  values: string[];
  /** 阈值模式（sxfl / total_sxfl 行可切「目标费率×」；cost_ent_appl 行可切
   *  「目标成本×」（PR-AT-20260923-013）；其他行恒 fixed）。 */
  value_mode: ValueMode;
}

let rowKeySeq = 0;
/** 生成本地行 key（自增即可，仅编辑器会话内唯一）。 */
function nextRowKey(): number {
  rowKeySeq += 1;
  return rowKeySeq;
}

/** 空白条件行（默认：数值指标 + 近3个工作日 + 注册成本 + > + 固定值）。 */
function emptyConditionRow(): ConditionRow {
  return {
    key: nextRowKey(),
    metric_type: "numeric",
    period: "3d",
    metric: "cost_register",
    operator: ">",
    value: "",
    values: [],
    value_mode: VALUE_MODE_FIXED,
  };
}

/** 库内条件 → 编辑器行（存量无 value_mode 键按 fixed 口径；
 *  多值枚举 value 数组落入 values，value 置空串）。 */
function toRow(c: ConditionItem): ConditionRow {
  const isMulti = Array.isArray(c.value);
  return {
    key: nextRowKey(),
    metric_type: c.metric_type,
    period: c.period,
    metric: c.metric,
    operator: c.operator,
    value: isMulti ? "" : String(c.value),
    values: isMulti ? (c.value as string[]).map(String) : [],
    value_mode: c.value_mode ?? VALUE_MODE_FIXED,
  };
}

/** 判断指标是否为多值枚举属性指标（如 present_hour 当天小时段，
 *  value 为枚举值数组，仅支持 = / !=，读作 ∈ / ∉）。 */
function isMultiEnumMetric(metric: string): boolean {
  return MULTI_VALUE_ENUM_METRIC_KEYS.has(metric);
}

/** 行指标可用的阈值模式选项（sxfl / total_sxfl → 固定值/目标费率×；
 *  cost_ent_appl → 固定值/目标成本×；其余指标 null = 纯固定值输入，
 *  不展示模式 select）。 */
function modeOptionsFor(metric: string): LabelOption[] | null {
  if (MULTIPLIER_ALLOWED_METRICS.includes(metric)) return VALUE_MODE_OPTIONS;
  if (COST_MULTIPLIER_ALLOWED_METRICS.includes(metric)) return COST_VALUE_MODE_OPTIONS;
  return null;
}

/** 倍率模式的换算基准（中文名 + 数值）：费率倍率取目标费率、成本倍率取目标成本
 * （PR-AT-20260923-013）；fixed 返回 null；对应基准未传时 value 为 undefined。 */
function multiplierBase(
  valueMode: ValueMode,
  targetFeilv?: number,
  targetCost?: number,
): { label: string; value: number | undefined } | null {
  if (valueMode === VALUE_MODE_TARGET_SXFL_MULTIPLIER) {
    return { label: "当前目标费率", value: targetFeilv };
  }
  if (valueMode === VALUE_MODE_TARGET_COST_MULTIPLIER) {
    return { label: "当前目标成本", value: targetCost };
  }
  return null;
}

/** 倍率模式生效阈值展示文本（目标费率/目标成本 × x；fixed、基准未传或 x 非法时
 *  返回 null 不展示）。 */
function effectiveThresholdText(
  row: ConditionRow,
  targetFeilv?: number,
  targetCost?: number,
): string | null {
  const base = multiplierBase(row.value_mode, targetFeilv, targetCost);
  if (base === null || base.value === undefined) return null;
  const x = Number.parseFloat(row.value);
  if (!Number.isFinite(x)) return null;
  return formatThreshold(base.value * x);
}

interface Props {
  open: boolean;
  /** edit 时传入被编辑记录；create 时为 null。 */
  record: WatchStrategyRecord | null;
  /** 父组件保存中（禁用按钮防重）。 */
  saving: boolean;
  /** 当前目标授信费率（费率倍率模式行内换算展示用；未传则不展示换算小字）。 */
  targetFeilv?: number;
  /** 当前目标成本（元，成本倍率模式行内换算展示用，PR-AT-20260923-013；
   *  未传则不展示换算小字）。 */
  targetCost?: number;
  onSubmit: (body: SaveWatchStrategyBody) => void;
  onCancel: () => void;
}

export default function WatchStrategyEditor({
  open,
  record,
  saving,
  targetFeilv,
  targetCost,
  onSubmit,
  onCancel,
}: Props) {
  const mode = record ? "edit" : "create";
  const [rows, setRows] = useState<ConditionRow[]>([emptyConditionRow()]);
  const [actionType, setActionType] = useState<string>("reduce_bid");
  /** 调/提价百分比（0~100，如 10 = 10%；提交时 /100 转小数）。 */
  const [actionPct, setActionPct] = useState<string>("10");

  // 每次打开弹层按 record 重建草稿；关闭即丢弃（与 FieldEditorModal 模式一致）。
  useEffect(() => {
    if (!open) return;
    if (record) {
      setRows(record.conditions.map(toRow));
      setActionType(record.action_type);
      setActionPct(
        record.action_value === null || record.action_value === undefined
          ? "10"
          : String(Math.round(record.action_value * 100)),
      );
    } else {
      setRows([emptyConditionRow()]);
      setActionType("reduce_bid");
      setActionPct("10");
    }
  }, [open, record]);

  // ESC 等同于取消。
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const isBidAction = actionType === "reduce_bid" || actionType === "increase_bid";

  function updateRow(key: number, patch: Partial<ConditionRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  /** 切换指标类别：重置指标为该类别首项；属性指标 period 置 null，数值指标默认 3d。
   *  value_mode 同步回落 fixed（属性首项 total_sxfl 虽支持倍率，切类别后仍默认
   *  固定值，由用户按需显式再选「目标费率×」）；多值枚举已选集合一并清空。 */
  function onChangeMetricType(key: number, nextType: MetricType) {
    if (nextType === "attribute") {
      updateRow(key, {
        metric_type: "attribute",
        period: null,
        metric: ATTRIBUTE_METRIC_OPTIONS[0].value,
        value: "",
        values: [],
        value_mode: VALUE_MODE_FIXED,
      });
    } else {
      updateRow(key, {
        metric_type: "numeric",
        period: "3d",
        metric: NUMERIC_METRIC_OPTIONS[0].value,
        value: "",
        values: [],
        value_mode: VALUE_MODE_FIXED,
      });
    }
  }

  /** 切换指标：切出倍率允许指标（费率类 sxfl / total_sxfl、成本类
   *  cost_ent_appl）时倍率模式强制回落 fixed（「目标费率×x」/「目标成本×x」
   *  仅对应类指标可用；两类集合互斥，sxfl↔cost 互切亦回落）。
   *  切入多值枚举（present_hour）时操作符重置为 =（∈）、已选集合清空
   *  （枚举仅支持 = / !=，残留 > 等操作符不合法）。 */
  function onChangeMetric(key: number, nextMetric: string) {
    const supportsMultiplier =
      MULTIPLIER_ALLOWED_METRICS.includes(nextMetric) ||
      COST_MULTIPLIER_ALLOWED_METRICS.includes(nextMetric);
    updateRow(key, {
      metric: nextMetric,
      ...(isMultiEnumMetric(nextMetric)
        ? { operator: "=", value: "", values: [] as string[] }
        : {}),
      ...(supportsMultiplier ? {} : { value_mode: VALUE_MODE_FIXED }),
    });
  }

  function onAddRow() {
    setRows((prev) =>
      prev.length >= MAX_CONDITIONS ? prev : [...prev, emptyConditionRow()],
    );
  }

  function onRemoveRow(key: number) {
    setRows((prev) =>
      prev.length <= 1 ? prev : prev.filter((r) => r.key !== key),
    );
  }

  /** 前端校验（口径同后端 service）：失败 Toast 并返回 null，否则产出提交体。 */
  function buildBody(): SaveWatchStrategyBody | null {
    if (rows.length < 1 || rows.length > MAX_CONDITIONS) {
      pushToast(`条件组须为 1~${MAX_CONDITIONS} 组`, "error");
      return null;
    }
    const conditions: ConditionItem[] = [];
    for (const [i, r] of rows.entries()) {
      // 多值枚举（present_hour）：value 为已选集合按注册表定义序归一后的
      // 字符串数组（非空），操作符仅 = / !=（∈ / ∉），恒 fixed
      if (isMultiEnumMetric(r.metric)) {
        const validValues = ENUM_METRIC_VALUES[r.metric] ?? [];
        const picked = validValues.filter((v) => r.values.includes(v));
        if (picked.length === 0) {
          pushToast(`第 ${i + 1} 条条件请至少选择 1 个枚举值`, "error");
          return null;
        }
        if (!ENUM_OPERATOR_OPTIONS.includes(r.operator)) {
          pushToast(`第 ${i + 1} 条条件操作符须为 = 或 !=`, "error");
          return null;
        }
        conditions.push({
          metric_type: r.metric_type,
          period: null,
          metric: r.metric,
          operator: r.operator,
          value: [...picked],
          value_mode: VALUE_MODE_FIXED,
        });
        continue;
      }
      const v = Number.parseFloat(r.value);
      if (!Number.isFinite(v)) {
        pushToast(`第 ${i + 1} 条条件阈值须为数字`, "error");
        return null;
      }
      if (r.metric_type === "numeric" && !r.period) {
        pushToast(`第 ${i + 1} 条条件须选择时间周期`, "error");
        return null;
      }
      // 阈值模式：授信费率类指标（sxfl / total_sxfl）允许「目标费率×x」、
      // 企业进件成本指标（cost_ent_appl）允许「目标成本×x」（PR-AT-20260923-013），
      // 其他行恒 fixed（同后端 DTO 联动口径）
      const supportsMultiplier =
        MULTIPLIER_ALLOWED_METRICS.includes(r.metric) ||
        COST_MULTIPLIER_ALLOWED_METRICS.includes(r.metric);
      const valueMode: ValueMode = supportsMultiplier ? r.value_mode : VALUE_MODE_FIXED;
      if (valueMode === VALUE_MODE_TARGET_SXFL_MULTIPLIER && v <= 0) {
        pushToast(`第 ${i + 1} 条条件为目标费率×x 模式，倍数 x 须为正数`, "error");
        return null;
      }
      if (valueMode === VALUE_MODE_TARGET_COST_MULTIPLIER && v <= 0) {
        pushToast(`第 ${i + 1} 条条件为目标成本×x 模式，倍数 x 须为正数`, "error");
        return null;
      }
      conditions.push({
        metric_type: r.metric_type,
        period: r.metric_type === "attribute" ? null : r.period,
        metric: r.metric,
        operator: r.operator,
        value: v,
        value_mode: valueMode,
      });
    }
    let actionValue: number | null = null;
    if (isBidAction) {
      const pct = Number.parseFloat(actionPct);
      if (!(Number.isFinite(pct) && pct > 0 && pct < 100)) {
        pushToast("调/提价百分比须为 (0,100) 开区间数字（如 10 表示 10%）", "error");
        return null;
      }
      // 百分比 → (0,1) 小数（如 10% → 0.1）。
      actionValue = pct / 100;
    }
    return {
      conditions,
      action_type: actionType as SaveWatchStrategyBody["action_type"],
      action_value: actionValue,
    };
  }

  function onSave() {
    if (saving) return;
    const body = buildBody();
    if (body) onSubmit(body);
  }

  const metricOptions = (r: ConditionRow) =>
    r.metric_type === "attribute"
      ? [...ATTRIBUTE_METRIC_OPTIONS, ...ENUM_ATTRIBUTE_METRIC_OPTIONS]
      : NUMERIC_METRIC_OPTIONS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "create" ? "新建盯盘服务" : "编辑盯盘服务"}
    >
      <div
        className="leo-card w-full max-w-[860px] overflow-hidden rounded-2xl border border-line-default bg-bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-subtle px-6 py-4">
          <div>
            <div className="font-display text-[15px] font-semibold text-text-emphasis">
              {mode === "create" ? "新建盯盘服务" : `编辑盯盘服务 #${record?.id}`}
            </div>
            <div className="mt-0.5 text-[11.5px] text-text-muted">
              满足全部条件（且）时对命中关键词执行操作；仅「打开」状态的策略参与判断。
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="leo-btn-ghost px-2 py-1.5 text-[12px]"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[68vh] overflow-y-auto px-6 py-5">
          {/* 条件组（1~5 行，组内 AND） */}
          <div className="mb-2 text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
            满足条件（组内且，最多 {MAX_CONDITIONS} 组）
          </div>
          <div className="flex flex-col gap-2">
            {rows.map((r, idx) => (
              <div
                key={r.key}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-line-subtle px-3 py-2.5"
              >
                <span className="w-5 shrink-0 text-center font-mono text-[11px] text-text-muted-weak">
                  {idx + 1}
                </span>
                {/* 指标类型 */}
                <div className="w-24 shrink-0">
                  <select
                    className="leo-select"
                    value={r.metric_type}
                    onChange={(e) => onChangeMetricType(r.key, e.target.value as MetricType)}
                    aria-label={`条件${idx + 1}指标类型`}
                  >
                    <option value="numeric">数值指标</option>
                    <option value="attribute">属性指标</option>
                  </select>
                </div>
                {/* 时间周期（属性指标禁用显示「—」） */}
                <div className="w-44 shrink-0">
                  {r.metric_type === "attribute" ? (
                    <select className="leo-select" disabled aria-label={`条件${idx + 1}时间周期`}>
                      <option>—</option>
                    </select>
                  ) : (
                    <select
                      className="leo-select"
                      value={r.period ?? "3d"}
                      onChange={(e) => updateRow(r.key, { period: e.target.value })}
                      aria-label={`条件${idx + 1}时间周期`}
                    >
                      {PERIOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {/* 指标 */}
                <div className="min-w-36 flex-1">
                  <select
                    className="leo-select"
                    value={r.metric}
                    onChange={(e) => onChangeMetric(r.key, e.target.value)}
                    aria-label={`条件${idx + 1}指标`}
                  >
                    {metricOptions(r).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* 操作符（多值枚举指标仅 = / !=，显示为 ∈ / ∉ 集合语义） */}
                <div className="w-16 shrink-0">
                  <select
                    className="leo-select"
                    value={r.operator}
                    onChange={(e) => updateRow(r.key, { operator: e.target.value })}
                    aria-label={`条件${idx + 1}操作符`}
                  >
                    {(isMultiEnumMetric(r.metric) ? ENUM_OPERATOR_OPTIONS : OPERATOR_OPTIONS).map((o) => (
                      <option key={o} value={o}>
                        {isMultiEnumMetric(r.metric) ? (o === "=" ? "∈" : "∉") : o}
                      </option>
                    ))}
                  </select>
                </div>
                {/* 阈值（多值枚举：阈值位不渲染控件，改由行内 chips 多选面板承载；
                    sxfl / total_sxfl 可选「目标费率×x」、cost_ent_appl 可选
                    「目标成本×x」（PR-AT-20260923-013），其余为固定值输入） */}
                {(() => {
                  // 多值枚举属性指标（present_hour）：chips 面板在行尾整行承载
                  if (isMultiEnumMetric(r.metric)) {
                    return null;
                  }
                  const modeOptions = modeOptionsFor(r.metric);
                  if (modeOptions === null) {
                    return (
                      <div className="w-28 shrink-0">
                        <input
                          type="number"
                          step="any"
                          className="leo-input"
                          value={r.value}
                          placeholder="阈值"
                          onChange={(e) => updateRow(r.key, { value: e.target.value })}
                          aria-label={`条件${idx + 1}阈值`}
                        />
                      </div>
                    );
                  }
                  const base = multiplierBase(r.value_mode, targetFeilv, targetCost);
                  const effective = effectiveThresholdText(r, targetFeilv, targetCost);
                  return (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <div className="w-28 shrink-0">
                        <select
                          className="leo-select"
                          value={r.value_mode}
                          onChange={(e) =>
                            updateRow(r.key, { value_mode: e.target.value as ValueMode })
                          }
                          aria-label={`条件${idx + 1}阈值模式`}
                        >
                          {modeOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-24 shrink-0">
                        <input
                          type="number"
                          step="any"
                          className="leo-input"
                          value={r.value}
                          placeholder={
                            r.value_mode !== VALUE_MODE_FIXED ? "倍数 x" : "阈值"
                          }
                          onChange={(e) => updateRow(r.key, { value: e.target.value })}
                          aria-label={`条件${idx + 1}阈值`}
                        />
                      </div>
                      {base !== null ? (
                        <span
                          className="whitespace-nowrap text-[11.5px] text-text-muted"
                          title={
                            effective !== null
                              ? `${base.label} ${formatThreshold(base.value ?? 0)} × ${r.value} = 生效阈值 ${effective}`
                              : `生效阈值 = ${base.label} × 输入倍数`
                          }
                        >
                          倍{effective !== null ? ` = ${effective}` : ""}
                        </span>
                      ) : null}
                    </div>
                  );
                })()}
                {/* 移除（仅 >1 行时可用） */}
                <button
                  type="button"
                  className="leo-btn-ghost is-danger shrink-0 px-2 py-1.5 text-[12px]"
                  onClick={() => onRemoveRow(r.key)}
                  disabled={rows.length <= 1}
                  aria-label={`移除条件${idx + 1}`}
                >
                  ✕
                </button>
                {/* 多值枚举（present_hour 当天小时段）：24 小时 chips 多选面板，
                    w-full 换行整行承载；= 读作 ∈（命中所选）、!= 读作 ∉（排除所选） */}
                {isMultiEnumMetric(r.metric) ? (
                  <div className="w-full pt-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2 text-[11.5px] text-text-muted">
                      <span>
                        小时（0~23 多选，{r.operator === "!=" ? "排除" : "命中"}所选）：
                      </span>
                      <button
                        type="button"
                        className="leo-btn-ghost px-1.5 py-0.5 text-[11px]"
                        onClick={() =>
                          updateRow(r.key, {
                            values: [...(ENUM_METRIC_VALUES[r.metric] ?? [])],
                          })
                        }
                      >
                        全选
                      </button>
                      <button
                        type="button"
                        className="leo-btn-ghost px-1.5 py-0.5 text-[11px]"
                        onClick={() => updateRow(r.key, { values: [] })}
                      >
                        清空
                      </button>
                      <span className="text-text-muted-weak">
                        已选 {r.values.length} 项
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(ENUM_METRIC_VALUES[r.metric] ?? []).map((v) => {
                        const selected = r.values.includes(v);
                        return (
                          <button
                            key={v}
                            type="button"
                            aria-pressed={selected}
                            className={`min-w-7 rounded-md border px-1.5 py-0.5 font-mono text-[11px] leading-4 transition-colors ${
                              selected
                                ? "border-accent-cta/60 bg-accent-cta/15 text-accent-cta"
                                : "border-line-subtle text-text-muted hover:border-line-strong hover:text-text-strong"
                            }`}
                            onClick={() =>
                              updateRow(r.key, {
                                values: selected
                                  ? r.values.filter((x) => x !== v)
                                  : [...r.values, v],
                              })
                            }
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <button
              type="button"
              className="leo-btn-ghost"
              onClick={onAddRow}
              disabled={rows.length >= MAX_CONDITIONS}
            >
              + 添加条件{rows.length >= MAX_CONDITIONS ? `（已达 ${MAX_CONDITIONS} 组上限）` : ""}
            </button>
          </div>

          {/* 执行操作 */}
          <div className="mb-2 mt-5 text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
            执行操作
          </div>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line-subtle px-3 py-2.5">
            <div className="w-36 shrink-0">
              <select
                className="leo-select"
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                aria-label="操作类型"
              >
                {ACTION_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {isBidAction ? (
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="w-24 shrink-0">
                  <input
                    type="number"
                    step="any"
                    className="leo-input"
                    value={actionPct}
                    onChange={(e) => setActionPct(e.target.value)}
                    aria-label="调/提价百分比"
                  />
                </div>
                <span className="text-[12.5px] text-text-muted">%</span>
              </div>
            ) : (
              <span className="text-[12px] text-text-muted">
                {actionType === "alert"
                  ? "仅记录预警命中，供后续告警推送（无参数）"
                  : "暂停该关键词投放（无参数）"}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-line-subtle bg-da-inset px-6 py-3.5">
          <button type="button" onClick={onCancel} className="leo-btn-ghost">
            取消
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="leo-cta-primary w-auto px-7"
          >
            {saving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
